import { useState, useEffect, useCallback } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Slide {
  image: string;
  alt: string;
  headline: string;
  subtitle: string;
}

const SLIDES: Slide[] = [
  {
    image: "/images/hero-exterior.webp",
    alt: "Exterior of Malak BnB Resort on a sunny day in Crystal Beach",
    headline: "Experience Serenity at Malak BnB Resort",
    subtitle: "Your Home Away From Home, Just Steps from Crystal Beach and Lake Erie",
  },
  {
    image: "/images/gallery-staircase-chandelier.webp",
    alt: "Outdoor heated pool with lounge chairs at Malak BnB Resort",
    headline: "Dive Into Relaxation at Our Poolside Oasis",
    subtitle: "Warm waters, cool evenings, and the sound of Lake Erie in the distance",
  },
  {
    image: "/images/room-elegant-suite.webp",
    alt: "Cozy queen bed with elegant warm lighting",
    headline: "Your Private Sanctuary Awaits",
    subtitle: "Plush bedding, refined décor, and every detail thoughtfully designed for your comfort",
  },
];

export function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setCurrent((c) => (c + 1) % SLIDES.length), []);
  const prev = useCallback(() => setCurrent((c) => (c - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [paused, next]);

  return (
    <section
      className="relative h-[90vh] min-h-[560px] overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slides */}
      {SLIDES.map((slide, i) => (
        <div
          key={slide.image}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === current ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
          aria-hidden={i !== current}
        >
          <img
            src={slide.image}
            alt={slide.alt}
            className="w-full h-full object-cover"
          />
        </div>
      ))}

      {/* Gradient overlay */}
      <div className="absolute inset-0 z-20 bg-gradient-to-t from-black/65 via-black/20 to-transparent" />

      {/* Slide content */}
      <div className="absolute inset-0 z-30 flex items-end pb-24 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl w-full">
          {SLIDES.map((slide, i) => (
            <div
              key={slide.headline}
              className={`max-w-2xl transition-all duration-700 ${
                i === current
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4 pointer-events-none absolute"
              }`}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading text-white leading-tight">
                {slide.headline}
              </h1>
              <p className="mt-4 text-lg text-white/85 max-w-xl leading-relaxed">
                {slide.subtitle}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/book"
                  className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
                >
                  Request to Book
                </Link>
                <Link
                  to="/rooms"
                  className="inline-flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm border border-white/30 px-8 py-3.5 text-sm font-semibold text-white hover:bg-white/25 transition-all"
                >
                  View Our Rooms
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Arrow navigation */}
      <button
        onClick={prev}
        aria-label="Previous slide"
        className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 z-40 w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white hover:bg-white/30 transition-all"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        onClick={next}
        aria-label="Next slide"
        className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 z-40 w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white hover:bg-white/30 transition-all"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* Dot indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-40 flex gap-7">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            aria-label={`Go to slide ${i + 1}`}
            className="group -m-2.5 flex items-center justify-center p-2.5"
          >
            <span
              className={`block h-1.5 rounded-full transition-all duration-300 ${
                i === current ? "w-8 bg-white" : "w-1.5 bg-white/50 group-hover:bg-white/75"
              }`}
            />
          </button>
        ))}
      </div>
    </section>
  );
}
